// web-helper/src/App.js
import React, { useState, lazy, Suspense, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LauncherWidget from './components/LauncherWidget';
import MobileNav from './components/MobileNav';
import MobileAdminPlaceholder from './components/MobileAdminPlaceholder';
import { useBreakpoint } from './hooks/useBreakpoint';
import { ChatSkeleton, AdminSkeleton } from './components/LoadingSkeleton';
import mixpanelService from './services/MixpanelService';
import * as MixpanelEvents from './services/MixpanelEvents';
import sessionTracker from './services/SessionTrackingService';
import PassphraseGate from './components/PassphraseGate';
import AnalyticsOptOut from './components/AnalyticsOptOut';
import './App.css';

// Lazy load heavy components for better initial load performance
const ChatInterface = lazy(() => import('./components/ChatInterface'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const Embed = lazy(() => import('./pages/Embed'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LocalAssistantHub = lazy(() => import('./pages/LocalAssistantHub'));
const WebAssistantHub = lazy(() => import('./pages/WebAssistantHub'));


// Admin Route Wrapper (shows placeholder below the dashboard's 1024px minimum)
function AdminRoute({ language }) {
  // The placeholder tells users the dashboard needs 1024px — gate on the
  // same width so 768-1023px screens don't get the unsupported layout
  const { width } = useBreakpoint();

  if (width < 1024) {
    return <MobileAdminPlaceholder language={language} />;
  }

  return (
    <Suspense fallback={<AdminSkeleton />}>
      <AdminDashboard />
    </Suspense>
  );
}

// Chat widget + mobile nav — only rendered on the /web route
function WebOnlyWidgets({ lang, setLang, isChatOpen, isOnline, isMobileNavOpen, onOpenChat, onCloseChat, onCloseMobileNav }) {
  const location = useLocation();
  // Normalize trailing slashes — the router matches /web/ but a strict
  // string comparison hid the chat launcher on shared /web/ links
  const path = location.pathname.replace(/\/+$/, '') || '/';
  if (path !== '/web') return null;

  return (
    <>
      <MobileNav
        isOpen={isMobileNavOpen}
        onClose={onCloseMobileNav}
        language={lang}
      />
      <LauncherWidget
        onOpenChat={onOpenChat}
        onCloseChat={onCloseChat}
        isChatOpen={isChatOpen}
        isOnline={isOnline}
        language={lang}
      />
      {isChatOpen && (
        <Suspense fallback={<ChatSkeleton />}>
          <ChatInterface
            isOpen={isChatOpen}
            onClose={onCloseChat}
            isOnline={isOnline}
            language={lang}
            setLanguage={setLang}
          />
        </Suspense>
      )}
    </>
  );
}

const LANGUAGE_KEY = 'compudopt_language';

function getStoredLanguage() {
  try {
    const stored = localStorage.getItem(LANGUAGE_KEY);
    return stored === 'es' ? 'es' : 'en';
  } catch (e) {
    return 'en';
  }
}

// Main App Component
function App() {
  // Language survives reloads — a Spanish speaker shouldn't have to
  // re-select their language every visit
  const [lang, setLangState] = useState(getStoredLanguage);
  // Wrapper that emits a Mixpanel "Language Changed" event whenever language actually changes
  const setLang = React.useCallback((next) => {
    setLangState((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      if (resolved !== prev) {
        try {
          localStorage.setItem(LANGUAGE_KEY, resolved);
        } catch (e) {
          // Storage unavailable (private mode) — preference lives for the session only
        }
        try {
          MixpanelEvents.trackLanguageChanged({
            previousLanguage: prev,
            newLanguage: resolved,
            conversationDepth: sessionTracker.getSessionStats().conversationDepth,
          });
        } catch (e) {
          // Swallow — analytics must never break UX
        }
      }
      return resolved;
    });
  }, []);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isOnline] = useState(true);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const sessionStartedRef = useRef(false);

  // Initialize Mixpanel and track session start
  useEffect(() => {
    // Initialize Mixpanel
    mixpanelService.initialize();

    // Track session start only once
    if (!sessionStartedRef.current) {
      sessionStartedRef.current = true;
      const session = sessionTracker.getSession();

      MixpanelEvents.trackSessionStart({
        sessionId: session.sessionId,
        language: lang,
        referrer: document.referrer || 'direct',
        isWidget: window.parent !== window,
        // Honest state: false when the user opted out or DNT is on
        hasConsent: !mixpanelService.hasOptedOut(),
      });
    }

    // Track session end on page unload
    const handleUnload = () => {
      const stats = sessionTracker.getSessionStats();
      MixpanelEvents.trackSessionEnd({
        durationSeconds: Math.round(stats.duration / 1000),
        messageCount: stats.conversationDepth,
        flowsCompleted: stats.exploredFlowsCount,
        exploreMoreClicks: stats.exploreMoreClicks,
        lastFlow: sessionTracker.getLastFlow(),
        feedbackGiven: false, // Can be tracked from feedback state
      });
      // Persist the real duration for the admin dashboard's average
      sessionTracker.recordCompletedSession(stats.duration);
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [lang]);

  const handleOpenChat = () => {
    setIsChatOpen(true);
    MixpanelEvents.trackChatOpened({
      source: 'launcher',
      language: lang,
      hasPreviousSession: sessionTracker.getSession().conversationDepth > 0,
    });
  };

  const handleCloseChat = () => {
    const stats = sessionTracker.getSessionStats();
    MixpanelEvents.trackChatClosed({
      messageCount: stats.conversationDepth,
      activeFlow: sessionTracker.getLastFlow(),
      hadFeedback: false, // Can be passed from ChatInterface
    });
    setIsChatOpen(false);
  };

  const handleToggleMobileNav = () => {
    setIsMobileNavOpen(!isMobileNavOpen);
  };

  const handleCloseMobileNav = () => {
    setIsMobileNavOpen(false);
  };

  const pageFallback = (
    <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
      Loading...
    </div>
  );

  return (
    <PassphraseGate>
      <Router>
        <div className="App" style={{
          minHeight: '100vh',
          background: 'var(--bg-secondary)'
        }}>
          <Routes>
          <Route
            path="/"
            element={
              <Suspense fallback={pageFallback}>
                <LandingPage />
              </Suspense>
            }
          />
          <Route
            path="/local"
            element={
              <Suspense fallback={pageFallback}>
                <LocalAssistantHub />
              </Suspense>
            }
          />
          <Route
            path="/web"
            element={
              <Suspense fallback={pageFallback}>
                <WebAssistantHub />
              </Suspense>
            }
          />
          <Route
            path="/releases"
            element={<Navigate to="/local" replace />}
          />
          <Route path="/admin" element={<AdminRoute language={lang} />} />
          <Route
            path="/embed"
            element={
              <Suspense fallback={<ChatSkeleton />}>
                <Embed />
              </Suspense>
            }
          />
          <Route
            path="*"
            element={
              <Suspense fallback={pageFallback}>
                <LandingPage />
              </Suspense>
            }
          />
        </Routes>

        {/* Chat widget — only on /web */}
        <WebOnlyWidgets
          lang={lang}
          setLang={setLang}
          isChatOpen={isChatOpen}
          isOnline={isOnline}
          isMobileNavOpen={isMobileNavOpen}
          onOpenChat={handleOpenChat}
          onCloseChat={handleCloseChat}
          onCloseMobileNav={handleCloseMobileNav}
        />

        {/* Analytics opt-out control (hidden on /embed and /admin) */}
        <AnalyticsOptOut language={lang} />

        </div>
      </Router>
    </PassphraseGate>
  );
}

export default App;
